import { Box, useTheme } from '@mui/material';
import { memo } from 'react';
import { BaseEdge, EdgeLabelRenderer, type EdgeProps, getSmoothStepPath } from 'reactflow';
import type { WorkflowEdgeData } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';
import { AddStepButton } from './add-step-button';
import { WorkflowAddStepMenu } from './workflow-add-step-menu';

/** A connection between two steps, with a + at its midpoint in a draft to insert a step there. */
const WorkflowEdgeComponent = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
  source,
  target,
}: EdgeProps<WorkflowEdgeData>) => {
  const theme = useTheme();
  const { view } = useWorkflowEditor();
  // Focus draws the way to the focused step in the accent colour and fades the rest; an open
  // search fades the connections into steps that don't match, as the steps themselves fade.
  let focusStyle = {};
  if (view.focusedId) {
    const lit = view.focusedIds.has(source) && view.focusedIds.has(target);
    focusStyle = lit
      ? { stroke: theme.palette.custom.OV.sys.primary, strokeWidth: 3 }
      : { opacity: 0.2 };
  } else if (view.searchActive && !view.matchIds.includes(target)) {
    focusStyle = { opacity: 0.2 };
  }
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  return (
    <>
      <BaseEdge path={path} style={{ ...style, ...focusStyle }} markerEnd={markerEnd} />
      {data?.editable ? (
        <EdgeLabelRenderer>
          <Box
            // the position changes every frame a card is dragged: an inline style, so it doesn't
            // mint a new stylesheet class each time
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
          >
            <WorkflowAddStepMenu
              insertion={{ kind: 'edge', edgeId: data.definitionEdgeId }}
              renderTrigger={(state) => <AddStepButton {...state} label="Add a step here" />}
            />
          </Box>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

export const WorkflowEdge = memo(WorkflowEdgeComponent);
